import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ShoppingBasket, ListChecks, ChefHat } from "lucide-react";
import { api } from "@/lib/api";
import { HOME_IDS } from "@/constants/testIds/meal";
import { toast } from "sonner";

const FALLBACK_MASCOT = "https://images.unsplash.com/photo-1669873438327-5a98df548c0b?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzd8MHwxfHNlYXJjaHwyfHxjdXRlJTIwY2F0JTIwY2hlZnxlbnwwfHx8fDE3ODM3Nzk5NTl8MA&ixlib=rb-4.1.0&q=85";

export default function Home() {
  const navigate = useNavigate();
  const [mascot, setMascot] = useState(FALLBACK_MASCOT);
  const [preparing, setPreparing] = useState(false);

  useEffect(() => {
    api.getMascots()
      .then((d) => {
        if (d.images && d.images.length > 0) setMascot(d.images[0]);
      })
      .catch(() => {});
  }, []);

  const handlePrepare = async () => {
    setPreparing(true);
    try {
      await api.resetList();
      toast.success("Nouvelle semaine, nouvelles courses !");
      navigate("/import");
    } catch {
      toast.error("Oups, impossible de réinitialiser");
    } finally {
      setPreparing(false);
    }
  };

  return (
    <main className="min-h-screen w-full px-6 py-12 md:py-8 relative overflow-hidden">
      {/* Ambient blobs */}
      <div className="pointer-events-none absolute -top-32 -left-32 w-96 h-96 rounded-full opacity-40" style={{ background: "radial-gradient(circle, #A3B18A55 0%, transparent 70%)" }} />
      <div className="pointer-events-none absolute -bottom-32 -right-32 w-[28rem] h-[28rem] rounded-full opacity-40" style={{ background: "radial-gradient(circle, #E07A5F44 0%, transparent 70%)" }} />

      {/* Top-right small button */}
      <div className="absolute top-6 right-6 z-10">
        <button
          data-testid={HOME_IDS.viewListBtn}
          onClick={() => navigate("/list")}
          className="chibi-btn-ghost inline-flex items-center gap-2"
        >
          <ListChecks className="w-5 h-5" />
          <span className="hidden sm:inline">Voir la liste de courses</span>
          <span className="sm:hidden">Ma liste</span>
        </button>
      </div>

      <div className="relative z-0 flex flex-col items-center justify-center min-h-[85vh] gap-8">
        {/* Mascot */}
        <div className="chibi-float" data-testid={HOME_IDS.mascot}>
          <div className="w-40 h-40 md:w-48 md:h-48 rounded-full overflow-hidden border-4 border-white shadow-[0_20px_60px_rgba(163,177,138,0.35)]">
            <img src={mascot} alt="Chibi chef mascot" className="w-full h-full object-cover" />
          </div>
        </div>

        {/* Title */}
        <div className="text-center chibi-pop">
          <h1
            data-testid={HOME_IDS.title}
            className="font-display font-semibold leading-[0.85] tracking-tight"
            style={{
              fontSize: "clamp(4rem, 15vh, 12rem)",
              color: "#4A3F35",
              letterSpacing: "-0.03em",
            }}
          >
            Meal <span style={{ color: "#E07A5F" }}>Planner</span>
          </h1>
          <p className="mt-4 text-lg md:text-xl font-soft text-[#4A3F35]/70">
            Planifie tes repas de la semaine avec douceur <ChefHat className="inline w-5 h-5 -mt-1" style={{ color: "#A3B18A" }} />
          </p>
        </div>

        {/* Primary CTA */}
        <button
          data-testid={HOME_IDS.prepareBtn}
          onClick={handlePrepare}
          disabled={preparing}
          className="chibi-btn-primary inline-flex items-center gap-3 text-lg mt-4"
        >
          <ShoppingBasket className="w-6 h-6" />
          {preparing ? "Préparation..." : "Préparer les courses de la semaine"}
        </button>
      </div>
    </main>
  );
}
